import { lerWorkspace, WORKSPACE_VAZIO, type EstadoDoWorkspace } from './estado';
import { ARRANJO_PADRAO, sanearArranjo, type ArranjoDeSessoes } from './arranjoDeSessoes';

/**
 * O que o navegador guarda entre uma página e outra.
 *
 * A URL continua sendo a verdade quando ela diz alguma coisa — é ela que faz
 * um link levar outra pessoa ao mesmo lugar. Mas navegar para Áudio e voltar
 * não pode apagar a pasta aberta, as abas e a sessão escolhida: quem clicou
 * numa aba do menu não pediu para recomeçar.
 *
 * Fica em `localStorage` porque é preferência de máquina, e por aparelho: o
 * que você tinha aberto no notebook não é o que quer ver no desktop.
 */

const CHAVE_ESTADO = 'remoto:workspace';
const CHAVE_RASCUNHOS = 'remoto:rascunhos';
const CHAVE_MENSAGENS = 'remoto:mensagens';
const CHAVE_EXIBICAO = 'remoto:exibicao';
const CHAVE_POR_SESSAO = 'remoto:workspace-sessoes';
const CHAVE_ARRANJO = 'remoto:sessoes-arranjo';

/** Teto do que guardamos: `localStorage` é pequeno e compartilhado. */
const MAXIMO_RASCUNHOS = 20;
const MAXIMO_POR_RASCUNHO = 200_000;

/**
 * Carimbo que nunca repete.
 *
 * Vinte rascunhos guardados no mesmo milissegundo empatam em `Date.now()`, e
 * aí a poda descarta por sorte em vez de por idade. Um contador que só anda
 * para frente mantém a ordem sem deixar de ser um horário de verdade.
 */
let ultimoCarimbo = 0;
function agora(): number {
  const t = Date.now();
  ultimoCarimbo = t > ultimoCarimbo ? t : ultimoCarimbo + 1;
  return ultimoCarimbo;
}

function ler<T>(chave: string, padrao: T): T {
  try {
    const cru = window.localStorage.getItem(chave);
    return cru ? ({ ...padrao, ...(JSON.parse(cru) as object) } as T) : padrao;
  } catch {
    // Modo anônimo, cota estourada, JSON estragado: nada disso pode derrubar
    // a tela. Sem memória o painel abre no começo, que é aceitável.
    return padrao;
  }
}

function gravar(chave: string, valor: unknown): void {
  try {
    window.localStorage.setItem(chave, JSON.stringify(valor));
  } catch {
    // Sem espaço ou sem permissão: seguir sem guardar.
  }
}

export function lembrarWorkspace(estado: EstadoDoWorkspace): void {
  gravar(CHAVE_ESTADO, estado);
}

/**
 * O estado com que a tela deve abrir.
 *
 * Se a URL trouxe qualquer coisa, ela manda — foi alguém que colou um link ou
 * apertou voltar, e sobrescrever isso com a memória tornaria os dois inúteis.
 * Só quando a URL está vazia é que a última visita vale.
 */
export function estadoInicial(busca: Record<string, unknown>): EstadoDoWorkspace {
  const daUrl = lerWorkspace(busca);
  const urlVazia =
    !daUrl.maquina &&
    !daUrl.pasta &&
    daUrl.abas.length === 0 &&
    !daUrl.sessao &&
    !busca['lado'] &&
    // A aba do celular conta como URL preenchida: sem isto, voltar para um
    // endereço que só tinha `cel` cairia no estado lembrado e desfaria o passo
    // que o botão voltar acabou de dar.
    !busca['cel'] &&
    // `prosa` pelo mesmo motivo, e ele é mais afiado aqui: sair da Prosa é
    // apagar o param, e sem esta linha o endereço resultante seria "vazio" —
    // a memória entraria e a Prosa voltaria por cima, desfazendo a saída.
    !busca['prosa'];
  if (!urlVazia) return daUrl;
  /*
   * A memória devolve no que a pessoa estava mexendo — máquina, pasta, abas,
   * sessão. A aba do celular não: ela é "onde estou olhando agora", e isso é
   * assunto do endereço.
   *
   * Guardá-la quebrava o botão voltar. Ao chegar em `/` sem parâmetro nenhum
   * — que é exatamente onde o voltar deixa quem começou na lista — a memória
   * ressuscitava `cel=chat` e a conversa voltava por cima da lista, como se o
   * passo não tivesse sido dado.
   */
  // `prosa: false` pelo mesmo motivo do `celular`: estar na Prosa é "onde
  // estou olhando agora", e isso é do endereço. Quem decide se ela ABRE por
  // padrão é `lerProsaNoCelular`, aplicada por navegação e não por memória —
  // senão o voltar cairia aqui e a Prosa ressuscitaria por cima da saída.
  return {
    ...ler<EstadoDoWorkspace>(CHAVE_ESTADO, WORKSPACE_VAZIO),
    celular: 'sessoes',
    prosa: false,
  };
}

/**
 * O que cada sessão tinha aberto.
 *
 * Explorador e abas são da sessão, não do painel: quem volta para a conversa
 * de ontem quer encontrar a pasta e os arquivos daquele trabalho, não os da
 * última coisa que olhou. A sessão escolhida e a aba lateral continuam sendo
 * do painel — essas são "onde estou", não "no que estou mexendo".
 */
export interface FatiaDaSessao {
  maquina: string;
  pasta: string;
  abas: string[];
  aba: string;
  em: number;
}

export function lembrarFatiaDaSessao(sessaoId: string, estado: EstadoDoWorkspace): void {
  if (!sessaoId) return;
  const mapa = ler<Record<string, FatiaDaSessao>>(CHAVE_POR_SESSAO, {});
  mapa[sessaoId] = {
    maquina: estado.maquina,
    pasta: estado.pasta,
    abas: estado.abas,
    aba: estado.aba,
    em: agora(),
  };
  gravar(CHAVE_POR_SESSAO, podar(mapa));
}

export function fatiaDaSessao(sessaoId: string): FatiaDaSessao | null {
  return ler<Record<string, FatiaDaSessao>>(CHAVE_POR_SESSAO, {})[sessaoId] ?? null;
}

/**
 * O que a conversa mostra, decidido no botão ⋯.
 *
 * Fica no aparelho, junto das larguras dos painéis: acompanhar uma sessão no
 * monitor grande e no notebook pede detalhe diferente, e isso não é assunto do
 * banco. O pensamento vem desligado — é longo, e quase sempre não é o que se
 * quer ler; quem quiser, liga.
 */
export interface Exibicao {
  ferramentas: boolean;
  pensamento: boolean;
  /** A lista de tarefas do TodoWrite; separada das ferramentas de propósito. */
  tarefas: boolean;
  /** Cartão de edição já vem com a comparação aberta. */
  diffAberto: boolean;
  /**
   * Segurar a tela acesa enquanto a conversa está aberta.
   *
   * Nasce ligada porque é o que o aparelho de leitura pede: acompanhar uma
   * sessão é ficar olhando sem tocar em nada, e trinta segundos depois a tela
   * apaga. Fica desligável porque a tranca custa bateria, e quem deixa o
   * tablet aberto o dia inteiro precisa poder abrir mão dela.
   */
  telaAcesa: boolean;
}

const EXIBICAO_PADRAO: Exibicao = {
  ferramentas: true,
  pensamento: false,
  tarefas: true,
  diffAberto: false,
  telaAcesa: true,
};

/**
 * O mesmo tipo, com outro padrão: a Prosa nasce sem o barulho do trabalho.
 *
 * `telaAcesa` continua ligada — quem está falando com o telefone na mão não
 * quer a tela apagando no meio da frase.
 */
export const EXIBICAO_PROSA: Exibicao = {
  ferramentas: false,
  pensamento: false,
  tarefas: false,
  diffAberto: false,
  telaAcesa: true,
};

/**
 * Dois perfis, UM mecanismo.
 *
 * A alternativa seria um segundo par de funções só para a Prosa, e aí as duas
 * telas passariam a ter regras de exibição que envelhecem separado. É a mesma
 * escolha do `Seletor`, que ganhou variante em vez de virar um segundo seletor.
 *
 * Chaves separadas de propósito: religar "comandos e resultados" na Prosa não
 * pode religá-los no painel de trabalho do desktop, que é onde eles são o
 * conteúdo principal.
 */
export type PerfilDeExibicao = 'painel' | 'prosa';

const CHAVE_DE_EXIBICAO: Record<PerfilDeExibicao, string> = {
  painel: CHAVE_EXIBICAO,
  prosa: 'remoto:exibicao-prosa',
};

const PADRAO_DE_EXIBICAO: Record<PerfilDeExibicao, Exibicao> = {
  painel: EXIBICAO_PADRAO,
  prosa: EXIBICAO_PROSA,
};

export function lerExibicao(perfil: PerfilDeExibicao = 'painel'): Exibicao {
  const padrao = PADRAO_DE_EXIBICAO[perfil];
  // O que já estava salvo pode não ter as chaves novas: o padrão completa.
  return { ...padrao, ...ler<Exibicao>(CHAVE_DE_EXIBICAO[perfil], padrao) };
}

export function gravarExibicao(valor: Exibicao, perfil: PerfilDeExibicao = 'painel'): void {
  gravar(CHAVE_DE_EXIBICAO[perfil], valor);
}

/**
 * Como a lista de sessões estava arrumada quando você saiu.
 *
 * Fica no aparelho, junto da exibição da conversa e das larguras dos painéis:
 * agrupar por projeto no monitor grande e por período no celular é o uso
 * normal, não uma inconsistência a corrigir.
 *
 * Guardar isto não é enfeite. Uma barra que esquece a escolha a cada F5 custa
 * mais do que não existir — a pessoa refaz três cliques por visita para voltar
 * ao lugar de onde nunca quis sair.
 *
 * O que voltou do `localStorage` passa por `sanearArranjo` antes de virar
 * estado: `ler` completa as chaves que faltam, mas não sabe se o VALOR ainda
 * existe, e um agrupamento de uma versão antiga do painel esvaziaria a lateral.
 */
export function lerArranjoDeSessoes(): ArranjoDeSessoes {
  return sanearArranjo(ler<ArranjoDeSessoes>(CHAVE_ARRANJO, ARRANJO_PADRAO));
}

export function gravarArranjoDeSessoes(valor: ArranjoDeSessoes): void {
  gravar(CHAVE_ARRANJO, valor);
}

/** Rascunho de arquivo, preso ao hash em que ele começou. */
export interface RascunhoDeArquivo {
  texto: string;
  /** O hash do arquivo quando a edição começou. */
  hash: string;
  em: number;
}

function podar<T>(mapa: Record<string, T & { em: number }>): Record<string, T & { em: number }> {
  const chaves = Object.keys(mapa).sort((a, b) => (mapa[b]?.em ?? 0) - (mapa[a]?.em ?? 0));
  const saida: Record<string, T & { em: number }> = {};
  for (const chave of chaves.slice(0, MAXIMO_RASCUNHOS)) {
    saida[chave] = mapa[chave] as T & { em: number };
  }
  return saida;
}

/**
 * O separador das duas partes da chave do rascunho.
 *
 * É o caractere nulo, como sempre foi: nenhum caminho de arquivo o contém, em
 * nenhum dos dois sistemas. O que mudou é ele não estar mais **cru no fonte**.
 * Com o byte lá dentro, o git classificava este arquivo como binário: uma
 * mudança de uma linha chegava à revisão como `Bin 9745 -> 10452 bytes`, sem
 * diff nenhum para ler. `String.fromCharCode` diz o mesmo em texto — e sem
 * barra invertida, que editor, heredoc e copiar-colar adoram colapsar.
 */
const SEPARADOR_DE_CHAVE = String.fromCharCode(0);

/**
 * A chave do rascunho inclui a máquina.
 *
 * `/home/claude/projetos/package.json` existe na VPS e no PC, e são arquivos
 * diferentes. Sem a máquina na chave, editar um restaurava por cima do outro.
 */
function chaveDoRascunho(maquinaId: string, caminho: string): string {
  return maquinaId + SEPARADOR_DE_CHAVE + caminho;
}

/**
 * Guarda o rascunho — **nunca vazio**.
 *
 * Um rascunho de texto vazio quase sempre é um Ctrl+A seguido de Delete sem
 * querer, e guardá-lo faz o arquivo reabrir em branco em toda visita, com o
 * hash ainda batendo: o Ctrl+S seguinte apaga o arquivo no disco de verdade.
 * Esvaziar um arquivo de propósito continua possível — só precisa ser salvo na
 * mesma visita, em vez de atravessar a navegação calado.
 */
export function guardarRascunho(
  maquinaId: string,
  caminho: string,
  texto: string,
  hash: string,
): void {
  if (texto.length > MAXIMO_POR_RASCUNHO) return;
  if (texto.length === 0) {
    esquecerRascunho(maquinaId, caminho);
    return;
  }
  const mapa = ler<Record<string, RascunhoDeArquivo>>(CHAVE_RASCUNHOS, {});
  mapa[chaveDoRascunho(maquinaId, caminho)] = { texto, hash, em: agora() };
  gravar(CHAVE_RASCUNHOS, podar(mapa));
}

export function esquecerRascunho(maquinaId: string, caminho: string): void {
  const mapa = ler<Record<string, RascunhoDeArquivo>>(CHAVE_RASCUNHOS, {});
  delete mapa[chaveDoRascunho(maquinaId, caminho)];
  gravar(CHAVE_RASCUNHOS, mapa);
}

/**
 * O rascunho guardado, **só se o arquivo não mudou no disco desde então**.
 *
 * Restaurar por cima de um arquivo que outra pessoa (ou a própria sessão do
 * Claude) alterou seria desfazer o trabalho dela sem avisar. Quando o hash não
 * bate, o rascunho é descartado: perder um texto que não foi salvo é ruim,
 * apagar em silêncio o que já está no disco é pior.
 *
 * Rascunho vazio é recusado também na leitura, e não só na escrita: quem já tem
 * um guardado do jeito antigo continuaria abrindo o arquivo em branco até
 * limpar o navegador.
 */
export function recuperarRascunho(
  maquinaId: string,
  caminho: string,
  hashAtual: string,
): string | null {
  const guardado = ler<Record<string, RascunhoDeArquivo>>(CHAVE_RASCUNHOS, {})[
    chaveDoRascunho(maquinaId, caminho)
  ];
  if (!guardado) return null;
  if (guardado.hash !== hashAtual || guardado.texto.length === 0) {
    esquecerRascunho(maquinaId, caminho);
    return null;
  }
  return guardado.texto;
}

/** Mensagem começada e não enviada, por sessão. */
export function guardarMensagem(sessaoId: string, texto: string): void {
  const mapa = ler<Record<string, { texto: string; em: number }>>(CHAVE_MENSAGENS, {});
  if (texto.trim().length === 0) delete mapa[sessaoId];
  else mapa[sessaoId] = { texto: texto.slice(0, 64_000), em: agora() };
  gravar(CHAVE_MENSAGENS, podar(mapa));
}

export function recuperarMensagem(sessaoId: string): string {
  return ler<Record<string, { texto: string; em: number }>>(CHAVE_MENSAGENS, {})[sessaoId]?.texto ?? '';
}

/**
 * O que a Prosa entendeu do último ditado — a rede de "parar e mandar".
 *
 * Slot PRÓPRIO, e não `guardarMensagem`, e o motivo é medido: o envio faz
 * `guardarMensagem(id, '')` no `onSuccess`, e esta função apaga a entrada
 * quando o texto é vazio. Reusar o rascunho do compositor faria **o próprio
 * envio limpar a rede**, em silêncio — exatamente a falha que ela existe para
 * impedir.
 *
 * Quem dita nome próprio, número de processo e número de OAB dita justamente
 * onde o Whisper erra. Então a cópia crua é escrita ANTES do despacho, nunca
 * depois: se a rede cair no meio, o que a pessoa falou já está no disco.
 * Quem apaga é a pessoa, ou a poda por idade — nunca o sucesso do envio.
 */
const CHAVE_DITADOS = 'remoto:ditados';

export function guardarDitado(sessaoId: string, texto: string): void {
  if (!sessaoId) return;
  const mapa = ler<Record<string, { texto: string; em: number }>>(CHAVE_DITADOS, {});
  if (texto.trim().length === 0) delete mapa[sessaoId];
  else mapa[sessaoId] = { texto: texto.slice(0, 64_000), em: agora() };
  gravar(CHAVE_DITADOS, podar(mapa));
}

export function recuperarDitado(sessaoId: string): string {
  return (
    ler<Record<string, { texto: string; em: number }>>(CHAVE_DITADOS, {})[sessaoId]?.texto ?? ''
  );
}

/** Some com a cópia crua — é gesto de quem leu e não precisa mais dela. */
export function esquecerDitado(sessaoId: string): void {
  guardarDitado(sessaoId, '');
}

/**
 * O menu lateral recolhido, por aparelho.
 *
 * Numa tela de notebook, quarenta pixels de rótulo valem mais na conversa do
 * que no menu; num monitor grande, não. Por isso a escolha é do aparelho e não
 * da conta — a mesma razão das larguras dos painéis.
 */
const CHAVE_MENU = 'remoto:menu-recolhido';

export function lerMenuRecolhido(): boolean {
  try {
    return window.localStorage.getItem(CHAVE_MENU) === '1';
  } catch {
    return false;
  }
}

export function gravarMenuRecolhido(recolhido: boolean): void {
  try {
    window.localStorage.setItem(CHAVE_MENU, recolhido ? '1' : '0');
  } catch {
    // Sem espaço ou sem permissão: seguir sem guardar.
  }
}

/**
 * Quais mestres têm a sub-árvore (vigiadas e anteriores) aberta, por aparelho.
 *
 * Nasce tudo recolhido: a lista é para achar a conversa, e o que a mestre
 * guarda dentro de si é consulta. Guarda os ids, e lixo no armazenamento
 * (outro tipo, outra versão) volta a "nada aberto".
 */
const CHAVE_MESTRES_EXPANDIDAS = 'remoto:mestres-expandidas';

export function lerMestresExpandidas(): string[] {
  try {
    const cru = window.localStorage.getItem(CHAVE_MESTRES_EXPANDIDAS);
    const valor: unknown = cru ? JSON.parse(cru) : [];
    return Array.isArray(valor) ? valor.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

export function gravarMestresExpandidas(ids: readonly string[]): void {
  try {
    window.localStorage.setItem(CHAVE_MESTRES_EXPANDIDAS, JSON.stringify(ids));
  } catch {
    // Sem espaço ou sem permissão: seguir sem guardar.
  }
}

/**
 * No celular, a Prosa é o que abre por padrão?
 *
 * Preferência de aparelho, irmã do menu recolhido: no telefone o pedido é
 * "abrir num bate-papo com o mestre", e no desktop o padrão continua sendo o
 * painel de trabalho. Nasce **ligada** — é o pedido do dono —, e por isso o
 * valor guardado é o "não" explícito: quem sair da Prosa e quiser que ela
 * pare de abrir sozinha grava `0`, e a ausência de chave continua querendo
 * dizer o padrão.
 *
 * O que ela NÃO faz: entrar em `estadoInicial`. Lá ela ressuscitaria a Prosa
 * por cima do botão voltar, que é a cicatriz do `cel` (ver `estadoInicial`).
 * Quem a aplica navega **uma vez, com `replace: true`**, e só quando a URL
 * não disse nada.
 */
const CHAVE_PROSA_NO_CELULAR = 'remoto:prosa-no-celular';

export function lerProsaNoCelular(): boolean {
  try {
    return window.localStorage.getItem(CHAVE_PROSA_NO_CELULAR) !== '0';
  } catch {
    return true;
  }
}

export function gravarProsaNoCelular(abrirNaProsa: boolean): void {
  try {
    window.localStorage.setItem(CHAVE_PROSA_NO_CELULAR, abrirNaProsa ? '1' : '0');
  } catch {
    // Sem espaço ou sem permissão: seguir sem guardar.
  }
}
